import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FrontHeader from '../components/common/FrontHeader';
import FrontFooter from '../components/common/FrontFooter';
import './PublicJobDetails.css';

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:5000/api'
    : '/api')
).replace(/\/+$/, '');

const text = value =>
  typeof value === 'string' ? value.trim() : '';

const getTime = value => {
  if (!value) return null;
  const result = new Date(value).getTime();
  return Number.isFinite(result) ? result : null;
};

const getNumber = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const result = Number(value);

  return Number.isFinite(result) && result >= 0
    ? result
    : null;
};

const formatDate = value => {
  const time = getTime(value);

  if (time === null) return 'Not specified';

  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(time);
};

const formatDateTime = value => {
  const time = getTime(value);

  if (time === null) return 'Not specified';

  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(time);
};

const formatMoney = (value, currency) => {
  const number = getNumber(value);

  if (number === null) return '';

  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      maximumFractionDigits: 2
    }).format(number);
  } catch {
    return `${currency} ${number.toLocaleString('en')}`;
  }
};

const getBudget = job => {
  if (job.budgetType === 'quotation') {
    return 'Request for quotation';
  }

  const currency = job.currency || 'USD';
  const amount = getNumber(job.budget);
  const min = getNumber(job.budgetMin);
  const max = getNumber(job.budgetMax);

  if (job.budgetType === 'hourly') {
    return amount === null
      ? 'Hourly rate negotiable'
      : `${formatMoney(amount, currency)} / hr`;
  }

  if (min !== null && max !== null) {
    return `${formatMoney(min, currency)} – ${formatMoney(max, currency)}`;
  }

  if (amount !== null) return formatMoney(amount, currency);
  if (min !== null) return `From ${formatMoney(min, currency)}`;
  if (max !== null) return `Up to ${formatMoney(max, currency)}`;

  return 'Budget not specified';
};

const getBudgetType = type => ({
  fixed: 'Fixed price',
  hourly: 'Hourly',
  quotation: 'Quotation requested'
}[type] || 'Not specified');

const DetailItem = ({ label, children }) => (
  <div className="pjd-detail-item">
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const PublicJobDetails = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [retry, setRetry] = useState(0);
  const [now, setNow] = useState(Date.now);
  const [copyStatus, setCopyStatus] = useState('');
  const [manualLink, setManualLink] = useState('');

  const copyTimerRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const loadJob = async () => {
      setLoading(true);
      setError('');
      setNotFound(false);
      setJob(null);
      setCopyStatus('');
      setManualLink('');
      clearTimeout(copyTimerRef.current);

      if (!/^[a-f0-9]{24}$/i.test(id || '')) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE}/public/rfqs/${encodeURIComponent(id)}`,
          {
            headers: { Accept: 'application/json' },
            signal: controller.signal,
            cache: 'no-store'
          }
        );

        const data = await response.json().catch(() => null);

        if (!active) return;

        if (response.status === 404) {
          setNotFound(true);
          return;
        }

        if (!response.ok || data?.success !== true || !data.rfq) {
          throw new Error(
            'Unable to load this job. Please try again.'
          );
        }

        setJob(data.rfq);
        setNow(Date.now());
      } catch (err) {
        if (active && err.name !== 'AbortError') {
          setError(
            'Unable to load this job. Check your connection and try again.'
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadJob();

    return () => {
      active = false;
      controller.abort();
    };
  }, [id, retry]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);

    return () => {
      clearInterval(interval);
      clearTimeout(copyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!job) return undefined;

    const previousTitle = document.title;

    document.title = `${text(job.serviceType) || 'Job'} at ${
      text(job.port) || 'Sea'
    } | SeaZex`;

    return () => {
      document.title = previousTitle;
    };
  }, [job]);

  const copyLink = async () => {
    const url = new URL(
      `/jobs/${encodeURIComponent(id)}`,
      window.location.origin
    ).toString();

    clearTimeout(copyTimerRef.current);
    setCopyStatus('');
    setManualLink('');

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }

      await navigator.clipboard.writeText(url);

      setCopyStatus('Link copied');
      copyTimerRef.current = setTimeout(() => {
        setCopyStatus('');
      }, 3000);
    } catch {
      setManualLink(url);
      setCopyStatus('Select and copy the link below.');
    }
  };

  const deadline = getTime(job?.deadline);
  const isOpen =
    job?.status === 'open' &&
    deadline !== null &&
    deadline > now;

  const daysRemaining = deadline === null
    ? null
    : Math.ceil((deadline - now) / 86400000);

  const deadlineLabel = !isOpen
    ? 'Closed for quotations'
    : daysRemaining <= 1
      ? 'Closes within 24 hours'
      : `${daysRemaining} days left`;

  const isProvider = user?.role === 'service-provider';

  const ownerName =
    text(job?.ownerId?.company) ||
    text(job?.ownerId?.name) ||
    'Ship owner';

  const attachmentCount = getNumber(job?.attachmentCount) || 0;
  const grt = getNumber(job?.grt);

  return (
    <div className="pjd-page">
      <FrontHeader />

      <main className="pjd-container">
        <nav className="pjd-breadcrumb" aria-label="Breadcrumb">
          <Link to="/available-jobs">
            <i className="fas fa-arrow-left" aria-hidden="true" />
            Available jobs
          </Link>

          <span aria-hidden="true">/</span>
          <span>Job details</span>
        </nav>

        {loading ? (
          <div className="pjd-state" role="status">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <h1>Loading job details</h1>
            <p>Fetching the service request.</p>
          </div>
        ) : notFound ? (
          <div className="pjd-state">
            <i className="fas fa-search" aria-hidden="true" />
            <h1>Job unavailable</h1>
            <p>
              This job may have been removed or may not be publicly available.
            </p>
            <Link className="pjd-button pjd-button-primary" to="/available-jobs">
              Browse available jobs
            </Link>
          </div>
        ) : error ? (
          <div className="pjd-state" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true" />
            <h1>Could not load this job</h1>
            <p>{error}</p>
            <button
              type="button"
              className="pjd-button pjd-button-primary"
              onClick={() => setRetry(value => value + 1)}
            >
              Try again
            </button>
          </div>
        ) : job ? (
          <>
            <header className="pjd-header">
              <div className="pjd-heading">
                <div className="pjd-badges">
                  <span className={`pjd-status ${isOpen ? 'is-open' : 'is-closed'}`}>
                    <span />
                    {isOpen ? 'Open for quotations' : 'Closed'}
                  </span>

                  <span className="pjd-type">
                    {getBudgetType(job.budgetType)}
                  </span>
                </div>

                <h1>{text(job.serviceType) || 'Maritime service request'}</h1>

                <div className="pjd-meta">
                  <span>
                    <i className="fas fa-ship" aria-hidden="true" />
                    {text(job.vesselName) || 'Vessel not specified'}
                  </span>

                  <span>
                    <i className="fas fa-map-marker-alt" aria-hidden="true" />
                    {text(job.port) || 'Port not specified'}
                  </span>

                  <span>
                    <i className="far fa-calendar-alt" aria-hidden="true" />
                    Posted {formatDate(job.createdAt)}
                  </span>
                </div>
              </div>

              <div className="pjd-share">
                <button
                  type="button"
                  className="pjd-button pjd-button-secondary"
                  onClick={copyLink}
                >
                  <i className="fas fa-link" aria-hidden="true" />
                  Copy job link
                </button>

                {copyStatus && (
                  <span className="pjd-copy-status" role="status">
                    {copyStatus}
                  </span>
                )}

                {manualLink && (
                  <input
                    className="pjd-copy-input"
                    aria-label="Job link"
                    value={manualLink}
                    readOnly
                    onFocus={event => event.target.select()}
                    onClick={event => event.target.select()}
                  />
                )}
              </div>
            </header>

            <div className="pjd-layout">
              <div className="pjd-main">
                <section className="pjd-panel">
                  <div className="pjd-section-heading">
                    <h2>
                      <i className="fas fa-align-left" aria-hidden="true" />
                      Scope of work
                    </h2>
                  </div>

                  <div className="pjd-description">
                    {text(job.description) || 'No description provided.'}
                  </div>
                </section>

                <section className="pjd-panel">
                  <div className="pjd-section-heading">
                    <h2>
                      <i className="fas fa-ship" aria-hidden="true" />
                      Vessel & service details
                    </h2>
                  </div>

                  <dl className="pjd-detail-grid">
                    <DetailItem label="Vessel">
                      {text(job.vesselName) || 'Not specified'}
                    </DetailItem>

                    <DetailItem label="Service required">
                      {text(job.serviceType) || 'Not specified'}
                    </DetailItem>

                    <DetailItem label="Service port">
                      {text(job.port) || 'Not specified'}
                    </DetailItem>

                    <DetailItem label="Next port">
                      {text(job.nextPort) || 'Not specified'}
                    </DetailItem>

                    <DetailItem label="Gross tonnage">
                      {grt === null ? 'Not specified' : grt.toLocaleString('en')}
                    </DetailItem>

                    <DetailItem label="Class approval">
                      {text(job.classApproval) || 'Not specified'}
                    </DetailItem>
                  </dl>
                </section>

                <details className="pjd-panel pjd-extra">
                  <summary>
                    <span>
                      <i className="far fa-file-alt" aria-hidden="true" />
                      Documents & request information
                      <small>
                        {attachmentCount} {attachmentCount === 1 ? 'document' : 'documents'}
                      </small>
                    </span>
                    <i className="fas fa-chevron-down" aria-hidden="true" />
                  </summary>

                  <div className="pjd-extra-body">
                    <dl className="pjd-detail-grid">
                      <DetailItem label="Job reference">
                        <code>{job._id}</code>
                      </DetailItem>

                      <DetailItem label="Last updated">
                        {formatDateTime(job.updatedAt || job.createdAt)}
                      </DetailItem>

                      <DetailItem label="Published">
                        {formatDateTime(job.createdAt)}
                      </DetailItem>

                      <DetailItem label="Documents">
                        {attachmentCount
                          ? `${attachmentCount} attached`
                          : 'No attachments'}
                      </DetailItem>
                    </dl>

                    {attachmentCount > 0 && (
                      <div className="pjd-documents-note">
                        <i className="fas fa-paperclip" aria-hidden="true" />

                        <p>
                          Supporting documents are available through the
                          service-provider job page.
                        </p>

                        {isProvider && isOpen && (
                          <Link to={`/service-provider/job/${job._id}`}>
                            Open job workspace
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                </details>
              </div>

              <aside className="pjd-sidebar" aria-label="Budget and quotation">
                <section className="pjd-panel pjd-budget-panel">
                  <span className="pjd-overline">JOB BUDGET</span>
                  <h2>{getBudget(job)}</h2>

                  <p className="pjd-budget-caption">
                    {getBudgetType(job.budgetType)}
                    <span>•</span>
                    {job.currency || 'USD'}
                  </p>

                  <div className={`pjd-deadline ${isOpen && daysRemaining <= 3 ? 'is-urgent' : ''}`}>
                    <i className="far fa-clock" aria-hidden="true" />
                    <div>
                      <strong>{deadlineLabel}</strong>
                      <span>{formatDateTime(job.deadline)}</span>
                    </div>
                  </div>

                  {authLoading ? (
                    <p className="pjd-action-note" role="status">
                      Checking your session…
                    </p>
                  ) : !isOpen ? (
                    <div className="pjd-closed-message">
                      This request is no longer accepting quotations.
                      <Link to="/available-jobs">Find another opportunity</Link>
                    </div>
                  ) : isProvider ? (
                    <>
                      <Link
                        className="pjd-button pjd-button-primary pjd-full"
                        to={`/service-provider/submit-quotation/${job._id}`}
                      >
                        Submit quotation
                        <i className="fas fa-arrow-right" aria-hidden="true" />
                      </Link>

                      <Link
                        className="pjd-button pjd-button-secondary pjd-full"
                        to={`/service-provider/job/${job._id}`}
                      >
                        Open job workspace
                      </Link>
                    </>
                  ) : user ? (
                    <p className="pjd-action-note">
                      Quotations can be submitted from a service-provider account.
                    </p>
                  ) : (
                    <>
                      <Link
                        className="pjd-button pjd-button-primary pjd-full"
                        to="/register"
                      >
                        Register to quote
                        <i className="fas fa-arrow-right" aria-hidden="true" />
                      </Link>

                      <p className="pjd-login-note">
                        Already a service provider? <Link to="/login">Sign in</Link>
                      </p>
                    </>
                  )}

                  <p className="pjd-small-note">
                    Deadline is displayed in your local time zone.
                  </p>
                </section>

                <section className="pjd-panel pjd-owner-panel">
                  <span className="pjd-overline">POSTED BY</span>

                  <div className="pjd-owner">
                    <span className="pjd-owner-icon">
                      <i className="far fa-building" aria-hidden="true" />
                    </span>

                    <div>
                      <strong>{ownerName}</strong>
                      <span>Ship owner</span>
                    </div>
                  </div>
                </section>
              </aside>
            </div>

            <div className="pjd-bottom">
              <Link to="/available-jobs">
                <i className="fas fa-arrow-left" aria-hidden="true" />
                Explore more opportunities
              </Link>

              <span>Review the full scope before submitting a quotation.</span>
            </div>
          </>
        ) : null}
      </main>

      <FrontFooter />
    </div>
  );
};

export default PublicJobDetails;